import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Atom, LogIn, LogOut, WifiOff, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useOnline } from "@/lib/offline";
import { links, roleLinks } from "./navLinks";
import BottomTabBar from "./BottomTabBar";
import InstallPrompt from "./InstallPrompt";

const roleTone = { "nav-mistakes": "border-red-400/60 text-red-200" };

const navCls = (isActive) => `flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium transition-colors duration-150 ${isActive ? "bg-white/10 text-cyan-300" : "text-slate-300 hover:bg-white/5 hover:text-slate-100"}`;

function DesktopMore({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const active = items.some((l) => pathname.startsWith(l.to));
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="nav-more-desktop" aria-expanded={open} className={navCls(active || open)}>
        <MoreHorizontal size={16} /> More
      </button>
      {open && (
        <div data-testid="nav-more-desktop-menu" className="absolute right-0 top-full z-40 mt-1 w-44 overflow-hidden rounded-md border border-white/10 bg-[#0b0f14] py-1 shadow-2xl">
          {items.map(({ to, label, icon: Icon, tid }) => (
            <NavLink key={to} to={to} data-testid={tid} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm ${isActive ? "bg-white/10 text-cyan-300" : "text-slate-300 hover:bg-white/5 hover:text-slate-100"}`}>
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ user, onLogout }) {
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-black/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        <NavLink to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="grid h-9 w-9 place-items-center rounded-md border border-cyan-400/40 bg-cyan-500/10 text-cyan-400"><Atom size={18} /></div>
          <div className="font-display text-lg font-bold tracking-tight">Chem<span className="text-cyan-400">Lab</span></div>
        </NavLink>
        <nav className="hidden items-center gap-0.5 lg:flex" data-testid="main-nav">
          {links.filter((l) => l.desktop).map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink key={to} to={to} end={end} data-testid={tid} className={({ isActive }) => navCls(isActive)}><Icon size={16} />{label}</NavLink>
          ))}
          <DesktopMore items={links.filter((l) => !l.desktop)} />
        </nav>
        <UserMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

function UserMenu({ user, onLogout }) {
  if (!user) {
    return (
      <Link to="/login" data-testid="nav-login" className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-3 py-2 text-xs font-semibold text-black hover:bg-cyan-400"><LogIn size={12} /> Sign in</Link>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {(roleLinks[user.role] || []).map(({ to, label, icon: Icon, tid }) => (
        <NavLink key={to} to={to} data-testid={tid} className={({ isActive }) => `hidden sm:inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${isActive ? roleTone[tid] || "border-cyan-400/60 text-cyan-200" : "border-white/10 text-slate-300 hover:text-slate-100"}`}>
          <Icon size={14} /> {label}
        </NavLink>
      ))}
      <button onClick={onLogout} data-testid="nav-logout" className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-white/10">
        <LogOut size={12} /> {user.name?.split(" ")[0]}
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const onLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen pb-20 text-slate-100 lg:pb-0">
      <Header user={user} onLogout={onLogout} />
      {!online && (
        <div className="border-b border-amber-400/30 bg-amber-500/10 px-4 py-2 text-center text-xs text-amber-100" data-testid="offline-banner">
          <WifiOff size={12} className="mr-1 inline" /> You're offline — <Link to="/offline" className="underline">saved lessons</Link> still work.
        </div>
      )}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10"><Outlet /></main>
      <footer className="border-t border-white/5 py-6 text-center text-xs text-slate-500 sm:py-8">
        <span className="font-mono-num">C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + ATP</span>
        <div className="mt-1">Made with curiosity · ChemLab</div>
      </footer>
      <InstallPrompt />
      <BottomTabBar user={user} />
    </div>
  );
}
